import buildClassName from '@utils/build-class-name';
import type { FC } from 'react';
import { memo, useDeferredValue, useEffect, useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import type { SortMatchResultType } from 'string-comparison';
import stringComparison from 'string-comparison';

import { useConversations } from '@/newstore/selectors/conversations';
import { useActiveConversation } from '@/newstore/selectors/shared';
import type { ConversationItem } from '@/newstore/slices/conversation';

import styles from './ConversationList.module.scss';
import ConversationListItem from './ConversationListItem';

interface OwnProps {
  searchQuery: string;
  activeChatId?: number;
}

interface StateProps {
  conversations: ConversationItem[];
}

const NonMemoConversationList: FC<OwnProps & StateProps> = ({ activeChatId, conversations, searchQuery }) => {
  const deferredQuery = useDeferredValue(searchQuery);
  const [, startTransition] = useTransition();
  const [filteredConversations, setFilteredConversations] = useState(conversations);

  useEffect(() => {
    startTransition(() => {
      const query = deferredQuery.trim();
      if (query === '') {
        setFilteredConversations(conversations);
        return;
      }

      const lowerCaseQuery = deferredQuery.toLowerCase();
      const RATING_THRESHOLD = 0;
      const INCLUSION_WEIGHT = 0.1;
      const sortedNames = new Map<string, SortMatchResultType>(
        stringComparison.diceCoefficient
          .sortMatch(
            deferredQuery,
            conversations.map((conversation) => conversation.name),
          )
          .map((sorted) => {
            const includesSearchQuery = sorted.member.toLowerCase().includes(lowerCaseQuery);
            if (includesSearchQuery) {
              sorted.rating += INCLUSION_WEIGHT;
            } else {
              sorted.rating -= INCLUSION_WEIGHT;
            }

            sorted.rating = sorted.rating < 0 ? 0 : sorted.rating;

            return [sorted.member, sorted];
          }),
      );

      setFilteredConversations(
        conversations
          .filter((conversation) => {
            const rating = sortedNames.get(conversation.name)?.rating;
            return deferredQuery.length === 1 || (rating && rating > RATING_THRESHOLD);
          })
          .sort((conversation1, conversation2) => {
            const rating1 = sortedNames.get(conversation1.name)?.rating;
            const rating2 = sortedNames.get(conversation2.name)?.rating;

            return (rating1 !== undefined && rating2 !== undefined && rating2 - rating1) || 0;
          }),
      );
    });
  }, [conversations, deferredQuery]);

  return (
    <Virtuoso
      className={buildClassName(styles.conversationList, 'custom-scroll')}
      data={filteredConversations}
      increaseViewportBy={350}
      itemContent={(_, conversation) => (
        <ConversationListItem id={conversation.id} active={activeChatId === conversation.id} />
      )}
    />
  );
};

const ConversationList = memo(function ConversationList({ searchQuery }: OwnProps) {
  const { conversations } = useConversations();
  const { conversation: activeConversation } = useActiveConversation();
  const navigate = useNavigate();

  useEffect(() => {
    if (activeConversation?.id) {
      navigate(`./conversations/${activeConversation.id}`, { viewTransition: true, relative: 'route' });
    }
  }, [navigate, activeConversation?.id]);

  return (
    <NonMemoConversationList
      searchQuery={searchQuery}
      conversations={conversations}
      activeChatId={activeConversation?.id}
    />
  );
});

export default ConversationList;
